import type { Item, Level, Vec2 } from '../hp/types';
import { hasTag, isCarpetLike, isPlant, isPrimitive, isStairs, isTablePlaneFurniture, isTableTopFurniture } from '../hp/furnitureClass';
import { isRegion } from '../hp/regions';
import { itemFootprint, localFootprint, type Footprints } from './footprint';
import { penetration } from './overlap';
import { verticalExtent, type SurfaceProbe } from './stacking';
export type { SurfaceProbe } from './stacking';

/**
 * Where a piece of furniture lands as it is moved: on the floor, or on top of what it is
 * dragged across — a vase climbs onto the table under the pointer and drops back to the floor
 * once it is pulled off the edge.
 *
 * This is the floor-plane half of Unity's `TopFurnitureHandler` (`GetTopFurniturePlaneOffset`
 * → `GetDistancesToFurniture`, `IsPassedRules`, `GetRaisedFloorsMaxDistance`), rule for rule:
 * only table-top pieces climb; only surface pieces carry; a piece never climbs onto something
 * much smaller than itself, onto a curtain, a beam, its own passengers, or a surface it does not
 * fit under the ceiling on; a rug lies on floors, slabs and rugs only; a large primitive slab is a
 * raised floor that everything stands on; and a piece jumps *up* onto a surface only while the
 * pointer is over that surface, although it settles down onto anything it is already above.
 *
 * What differs is the instrument. Unity raycasts against mesh colliders; the web asks oriented
 * footprint boxes whether the piece is over a surface and takes the surface height from an
 * optional `SurfaceProbe` that reads the loaded meshes under a few points around the hand — that
 * is how a pillow lands on a chair's seat and not at the height of its back — falling back to
 * the box top when no mesh is loaded. A layered probe preserves interior shelves and the clear
 * space up to the next underside, matching Unity's multiple-intersection handling.
 */

/** A primitive slab this large is a raised floor: anything dragged over it stands on it. */
const RAISED_FLOOR_MIN_AREA = 8;
/** Real furniture this large — a stage, a podium — carries even pieces that are not table-top items. */
const LIKE_RAISED_FLOOR_MIN_AREA = 30;
/** A piece may not climb onto something whose footprint is smaller than its own by more than this. */
const AREA_RATIO = 1.65;
/** The room kept under the ceiling above a piece that has climbed. */
const CEILING_CLEARANCE = .01;
/** How far the hand samples spread around the grab point, capped at a quarter of the piece's smaller side. */
const HAND_SPREAD = .05;
/** A bottom this close under a surface already rests on it and need not be pointed at to stay. */
const CONTACT = .04;

export interface Point3 { x: number; y: number; z: number }
/** The pointer during a move: the plan point under it (plan view, drops), or the camera ray through it (3D). */
export type LandingPointer =
  | { kind: 'plan'; point: Vec2 }
  | { kind: 'ray'; origin: Point3; direction: Point3 };
export interface LandingOptions {
  pointer?: LandingPointer;
  probe?: SurfaceProbe;
  footprints?: Footprints;
  /** Ids that travel with the piece — what stands on it — and so can never be its surface. */
  exclude?: ReadonlySet<string>;
  /** Where the piece's bottom is right now, once a gesture has already lifted or dropped it. */
  currentElevation?: number;
  /** The hand's offset from the piece's centre, in plan space. */
  grab?: Vec2;
}

export interface Landing { elevation: number; baseId: string | null }

/**
 * A pointer through an open cabinet's cavity has no useful floor intersection. Project it to
 * the cabinet's depth, but only when an actual interior shelf can carry the whole moved item.
 * The caller supplies the visible furniture hit, so walls and closed objects cannot be crossed.
 */
export function pointedShelfPosition(level: Level, item: Item, pointer: LandingPointer, baseId: string,
  options: Omit<LandingOptions, 'pointer'> = {}): Vec2 | null {
  if (item.placement !== 'floor' || !isTableTopFurniture(item) || pointer.kind !== 'ray' || !options.probe?.layers) return null;
  const base = level.items.find(value => value.id === baseId);
  if (!base || base.id === item.id || options.exclude?.has(baseId) || !passesRules(item, base)) return null;
  const { origin, direction } = pointer, horizontal = direction.x ** 2 + direction.z ** 2;
  if (horizontal < 1e-6 || !pointedAt(base, pointer, options.footprints).hit) return null;
  const bounds = localFootprint(base, options.footprints), c = Math.cos(base.rotation), s = Math.sin(base.rotation);
  const u = (bounds.minX + bounds.maxX) / 2, v = (bounds.minZ + bounds.maxZ) / 2;
  const centre = { x: base.position.x + u * c - v * s, z: base.position.z + u * s + v * c };
  const distance = ((centre.x - origin.x) * direction.x + (centre.z - origin.z) * direction.z) / horizontal;
  if (distance <= 0) return null;
  // A floor projection can stretch the initial grab far beyond the small item's own outline.
  const half = Math.hypot(item.size.w, item.size.d) / 2;
  const grab = options.grab ?? { x: 0, z: 0 };
  const position = { x: origin.x + direction.x * distance - Math.max(-half, Math.min(half, grab.x)),
    z: origin.z + direction.z * distance - Math.max(-half, Math.min(half, grab.z)) };
  // A floor-projected grab can exceed the available depth of a narrow shelf. Keep the
  // full footprint inside the target outline before testing its actual mesh support.
  const offsets = itemFootprint({ ...item, position: { x: 0, z: 0 } }, options.footprints)
    .map(point => ({ x: point.x * c + point.z * s, z: -point.x * s + point.z * c }));
  const minX = bounds.minX - Math.min(...offsets.map(point => point.x)) + .001;
  const maxX = bounds.maxX - Math.max(...offsets.map(point => point.x)) - .001;
  const minZ = bounds.minZ - Math.min(...offsets.map(point => point.z)) + .001;
  const maxZ = bounds.maxZ - Math.max(...offsets.map(point => point.z)) - .001;
  if (minX > maxX || minZ > maxZ) return null;
  const local = toLocal(base, position), x = Math.max(minX, Math.min(maxX, local.x)), z = Math.max(minZ, Math.min(maxZ, local.z));
  position.x = base.position.x + x * c - z * s; position.z = base.position.z + x * s + z * c;
  const layers = options.probe.layers(base, [position, ...itemFootprint({ ...item, position }, options.footprints)]);
  if (!layers?.some(layer => Number.isFinite(layer.ceiling) && layer.supported && layer.accessible)) return null;
  const landing = landingElevation({ ...level, items: [base] }, item, position, { ...options, pointer });
  return layers.some(layer => Number.isFinite(layer.ceiling) && layer.supported && layer.accessible
    && Math.abs(layer.top - landing.elevation) < 1e-4) ? position : null;
}

/** Unity `GetUserDimensions()` x·z: the catalog footprint, not the measured one. */
const area = (item: Item) => item.size.w * item.size.d;

/** A plan point in the base's own frame: along its width and its depth from its centre. */
function toLocal(base: Item, point: Vec2): Vec2 {
  const c = Math.cos(base.rotation), s = Math.sin(base.rotation);
  const dx = point.x - base.position.x, dz = point.z - base.position.z;
  return { x: dx * c + dz * s, z: -dx * s + dz * c };
}

function insideFootprint(base: Item, point: Vec2, footprints?: Footprints): boolean {
  const bounds = localFootprint(base, footprints), local = toLocal(base, point);
  return local.x >= bounds.minX && local.x <= bounds.maxX && local.z >= bounds.minZ && local.z <= bounds.maxZ;
}

/** The height at which the camera ray enters the base's box, or null when it misses. */
function rayEntryHeight(base: Item, ray: { origin: Point3; direction: Point3 }, footprints?: Footprints): number | null {
  const c = Math.cos(base.rotation), s = Math.sin(base.rotation);
  const ox = ray.origin.x - base.position.x, oz = ray.origin.z - base.position.z;
  const origin = { x: ox * c + oz * s, y: ray.origin.y, z: -ox * s + oz * c };
  const dir = { x: ray.direction.x * c + ray.direction.z * s, y: ray.direction.y, z: -ray.direction.x * s + ray.direction.z * c };
  const b = localFootprint(base, footprints), { bottom, top } = verticalExtent(base);
  let enter = 0, exit = Infinity;
  for (const [o, d, lo, hi] of [[origin.x, dir.x, b.minX, b.maxX], [origin.y, dir.y, bottom, top], [origin.z, dir.z, b.minZ, b.maxZ]]) {
    if (Math.abs(d) < 1e-12) { if (o < lo || o > hi) return null; continue; }
    let t1 = (lo - o) / d, t2 = (hi - o) / d;
    if (t1 > t2) [t1, t2] = [t2, t1];
    enter = Math.max(enter, t1); exit = Math.min(exit, t2);
    if (enter > exit) return null;
  }
  return origin.y + dir.y * enter;
}

/** Unity `GetFurnitureListPointedByCursor`: every piece the pointer's ray passes through counts. */
function pointedAt(base: Item, pointer: LandingPointer | undefined, footprints?: Footprints): { hit: boolean; height?: number } {
  if (!pointer) return { hit: false };
  if (pointer.kind === 'plan') return { hit: insideFootprint(base, pointer.point, footprints) };
  const height = rayEntryHeight(base, pointer, footprints);
  return height === null ? { hit: false } : { hit: true, height };
}

/** Unity `IsPassedRules` for a floor piece moved on the floor plane. */
function passesRules(item: Item, base: Item): boolean {
  if (hasTag(base, 'InteriorWall') || hasTag(base, 'Curtains') || hasTag(base, 'CeilingBeams') || hasTag(base, 'SloppedCeiling')) return false;
  if (!isTablePlaneFurniture(base)) return false;
  // Floor furniture may not carry something much bigger than itself; a plant's crown is exempt.
  if (base.placement === 'floor' && !(isPrimitive(item) && isPrimitive(base)) && !isPlant(item) && area(item) > AREA_RATIO * area(base)) return false;
  // A floor panel stacks on floor panels only.
  if (isRegion(item) && !isRegion(base)) return false;
  if (isCarpetLike(item)) return isCarpetLike(base) || isPrimitive(base);
  if (hasTag(item, 'WashersDryers') && isStairs(base)) return false;
  return true;
}

/**
 * The elevation `item` takes once its centre is at `position`, and the piece it stands on there.
 * Nothing found means the floor. `level` holds the surfaces as they are; the item itself, its
 * passengers (`exclude`) and ceiling pieces are never surfaces.
 */
export function landingElevation(level: Level, item: Item, position: Vec2, options: LandingOptions = {}): Landing {
  if (item.placement !== 'floor') return { elevation: item.elevation, baseId: null };
  const { footprints, probe, pointer, exclude } = options;
  const moved: Item = { ...item, position };
  const current = options.currentElevation ?? item.elevation;
  const height = item.size.h, top = current + height;
  const corners = itemFootprint(moved, footprints);
  const samples: Vec2[] = [position, ...corners];
  // Unity `ChangeMaxValueByHitPoint`: the surface under the hand decides the height, not the box.
  const hand = options.grab ? { x: position.x + options.grab.x, z: position.z + options.grab.z }
    : pointer?.kind === 'plan' ? pointer.point : position;
  const spread = Math.min(HAND_SPREAD, .25 * Math.min(item.size.w, item.size.d));
  const c = Math.cos(item.rotation), s = Math.sin(item.rotation);
  const handSamples: Vec2[] = [hand, { x: hand.x + spread * c, z: hand.z + spread * s }, { x: hand.x - spread * c, z: hand.z - spread * s },
    { x: hand.x - spread * s, z: hand.z + spread * c }, { x: hand.x + spread * s, z: hand.z - spread * c }];

  const others = level.items.filter(base => base.id !== item.id && !exclude?.has(base.id) && base.placement !== 'ceiling');
  const candidates: Array<{ id: string; top: number }> = [];
  const likeRaisedFloor = new Set<string>();
  for (const base of others) {
    if (!isPrimitive(base) && area(base) > LIKE_RAISED_FLOOR_MIN_AREA) likeRaisedFloor.add(base.id);
    if (!isPrimitive(base) || area(base) < RAISED_FLOOR_MIN_AREA) continue;
    if (penetration(corners, itemFootprint(base, footprints)) <= 0) continue;
    const extent = verticalExtent(base);
    if (extent.bottom <= top) candidates.push({ id: base.id, top: extent.top });
  }
  const climber = isTableTopFurniture(item);
  if (climber || likeRaisedFloor.size) for (const base of others) {
    if ((!climber && !likeRaisedFloor.has(base.id)) || !passesRules(item, base)) continue;
    const bothPrimitive = isPrimitive(item) && isPrimitive(base);
    const under = bothPrimitive ? penetration(corners, itemFootprint(base, footprints)) > 0
      : samples.some(point => insideFootprint(base, point, footprints));
    if (!under) continue;
    const extent = verticalExtent(base);
    const pointed = pointedAt(base, pointer, footprints);
    const meshLayers = probe?.layers?.(base, samples);
    if (meshLayers && !meshLayers.length) continue;
    const layered = !!meshLayers?.some(layer => Number.isFinite(layer.ceiling));
    // A top-down plan cannot select a different interior shelf. Keep the existing support until
    // the item leaves it, instead of interpreting every small move as a jump onto the roof.
    const retained = layered && pointer?.kind !== 'ray'
      ? meshLayers!.find(layer => layer.supported && Math.abs(layer.top - current) <= CONTACT) : undefined;
    const surfaces = layered ? meshLayers! : [{ bottom: extent.bottom,
      top: probe?.(base, handSamples) ?? probe?.(base, samples) ?? extent.top,
      ceiling: Infinity, supported: true, accessible: true }];
    for (const layer of surfaces) {
      const surface = layer.top, interior = Number.isFinite(layer.ceiling);
      if (retained && layer !== retained) continue;
      if (interior && (!layer.supported || !layer.accessible) && Math.abs(current - surface) > CONTACT) continue;
      if (interior && !layer.supported) continue;
      if (Math.min(level.height, layer.ceiling) - CEILING_CLEARANCE < surface + height) continue;
      // A piece jumps up only onto what the pointer is over; it settles onto anything already below.
      if (!pointed.hit && current + CONTACT < surface) continue;
      // Layered furniture uses each shelf's midpoint, never the enclosing cabinet's bottom.
      // Otherwise a low pointer would qualify every shelf, including the cabinet roof.
      if (pointer?.kind === 'ray' && (layered ? pointed.hit : layer.bottom > top && current + CONTACT < surface)
        && !(pointed.height !== undefined && (layer.bottom + surface) / 2 <= pointed.height)) continue;
      candidates.push({ id: base.id, top: surface });
    }
  }
  if (!candidates.length) return { elevation: 0, baseId: null };
  const best = candidates.reduce((a, b) => b.top > a.top ? b : a);
  // Unity "offset height control": a piece that would not fit under the ceiling stays on the floor.
  if (level.height < height + best.top) return { elevation: 0, baseId: null };
  return { elevation: Math.max(0, best.top), baseId: best.id };
}
